import { Auth } from "@aws-amplify/auth"
import { useCallback, useState } from "react"

/**
 *
 * @param url if passing a function, you may need to wrap that function in useCallback first
 * @param method
 * @returns
 */
const useLazyQuery = <InputBody, OutputBody>(
	url: string | ((parameters: Parameters<InputBody>) => string),
	method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE" = "GET",
) => {
	const [data, setData] = useState<OutputBody | undefined>(undefined)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | undefined>(undefined)
	//wrap the query in useCallback so the function doesn't change and trigger other useEffect hooks
	const rawQuery = async (parameters?: Parameters<InputBody>): Promise<OutputBody> => {
		const urlString = typeof url === "function" ? url(parameters!) : url
		setLoading(true)
		try {
			const session = await Auth.currentSession()
			const body = await convertBody(parameters?.body)
			const headers = { Authorization: session.getIdToken().getJwtToken() }
			const response = await fetch(urlString, { headers, method, body })
			if (response.status === 200 || response.status === 201) {
				setError(undefined)
				const body = await response.text()
				let convertedBody: OutputBody
				if (parameters?.rawResponse) {
					convertedBody = body as OutputBody
				} else {
					convertedBody = body && JSON.parse(body)
				}
				setData(convertedBody)
				setLoading(false)
				return convertedBody
			} else {
				//TODO remove?
				if (response.status === 401) {
					console.log("Auth error")
					console.log(response)
				}
				setData(undefined)
				const json = response.body && (await response.json())
				console.error(json?.developerError || response)
				setError(json?.userError || json?.message || response.statusText)
				setLoading(false)
				throw Error(json?.developerError || response)
			}
		} catch (e) {
			console.error(e)
			setError("System Error")
			setLoading(false)
			throw e
		}
	}
	const query = useCallback(rawQuery, [url, method])
	return { query, data, loading, error }
}
export default useLazyQuery

export interface Parameters<InputBody> {
	id?: string
	body?: InputBody | string
	rawResponse?: boolean
}

const convertBody = async (body?: any): Promise<string | undefined> => {
	if (body instanceof Blob) return await body.text()
	return JSON.stringify(body)
}
